/**
 * An item's history across resubmissions: the same work rejected (for conflicts, say),
 * resubmitted and finally landed is one chain of attempts, so repeated rebase rounds are
 * visible. Works for any adapter's items. Pure, so it is tested.
 *
 * Attempts belong together when their titles match once the "(and N more)" suffix and case
 * are set aside: a resubmission usually comes from a fresh worktree, but its commits keep
 * their subjects. An item with no real title ("Submission <id>") chains by its source (`by`)
 * instead, or stands alone. A chain ends when an attempt passes: the same title later is new work.
 * An attempt follows a rejection, so it joins a chain only once the chain's last attempt
 * ended: two submissions with one title in flight at the same time (two agents' "fix:
 * typecheck", say) are separate work.
 */
import type { Pipeline, PipelineItem, Ts } from '@orgus/protocol';
import { plural } from '@orgus/ui';

export type ChainOutcome = 'landed' | 'in-flight' | 'failed' | 'cancelled';

export interface Chain {
  key: string;
  title: string;
  /** Oldest first. */
  attempts: PipelineItem[];
  /** Attempts that failed. */
  rejected: number;
  outcome: ChainOutcome;
  /** The newest attempt. */
  latest: PipelineItem;
  /** When the newest attempt started or ended, whichever is later. */
  lastAt: Ts;
}

const PLACEHOLDER = /^Submission \S+$/;

/** A title with "(and 2 more)", case and spacing set aside. */
export function normTitle(t: string): string {
  return t
    .replace(/\s*\(and \d+ more\)\s*$/i, '')
    .replace(/\s+/g, ' ')
    .trim()
    .toLowerCase();
}

function keyOf(i: PipelineItem): string {
  if (!PLACEHOLDER.test(i.title)) return `t:${normTitle(i.title)}`;
  return i.by ? `by:${i.by}` : `id:${i.id}`;
}

const atOf = (i: PipelineItem): Ts => i.startedAt ?? i.endedAt ?? 0;

function outcomeOf(i: PipelineItem): ChainOutcome {
  if (i.state === 'passed') return 'landed';
  if (i.state === 'failed') return 'failed';
  if (i.state === 'cancelled') return 'cancelled';
  return 'in-flight';
}

/** Every chain of a pipeline (in flight and recent items), the most recently active first. */
export function pipelineChains(p: Pick<Pipeline, 'items' | 'recent'>): Chain[] {
  const seen = new Set<string>();
  const all: PipelineItem[] = [];
  for (const i of [...p.items, ...p.recent]) {
    if (seen.has(i.id)) continue;
    seen.add(i.id);
    all.push(i);
  }
  all.sort((a, b) => atOf(a) - atOf(b) || a.id.localeCompare(b.id));
  const byKey = new Map<string, PipelineItem[][]>();
  for (const i of all) {
    const k = keyOf(i);
    let list = byKey.get(k);
    if (!list) byKey.set(k, (list = []));
    // The newest chain of this title whose last attempt was rejected or withdrawn before this one started.
    const cur = list.findLast((c) => {
      const last = c[c.length - 1] as PipelineItem;
      return (last.state === 'failed' || last.state === 'cancelled') && (last.endedAt ?? atOf(last)) <= atOf(i);
    });
    if (cur) cur.push(i);
    else list.push([i]);
  }
  return [...byKey.values()]
    .flat()
    .map((attempts): Chain => {
      const latest = attempts[attempts.length - 1] as PipelineItem;
      return {
        key: `${keyOf(latest)}#${(attempts[0] as PipelineItem).id}`,
        title: latest.title,
        attempts,
        rejected: attempts.filter((a) => a.state === 'failed').length,
        outcome: outcomeOf(latest),
        latest,
        lastAt: Math.max(atOf(latest), latest.endedAt ?? 0),
      };
    })
    .sort((a, b) => b.lastAt - a.lastAt || a.key.localeCompare(b.key));
}

/** Chains with more than one attempt: the resubmitted work. */
export function resubmitted(chains: readonly Chain[]): Chain[] {
  return chains.filter((c) => c.attempts.length > 1);
}

/** The chain an item belongs to, and its 1-based attempt number in it. */
export function attemptOf(chains: readonly Chain[], itemId: string): { chain: Chain; n: number } | null {
  for (const chain of chains) {
    const i = chain.attempts.findIndex((a) => a.id === itemId);
    if (i >= 0) return { chain, n: i + 1 };
  }
  return null;
}

/** "Attempt 3: 2 earlier attempts were rejected" (only the attempts before the n-th count). */
export function attemptTip(c: Chain, n: number): string {
  const earlier = c.attempts.slice(0, n - 1).filter((a) => a.state === 'failed').length;
  return `Attempt ${n}: ${earlier ? `${earlier} earlier ${earlier === 1 ? 'attempt was' : 'attempts were'} rejected` : `${plural(n - 1, 'earlier attempt')}, none rejected`}`;
}

/** Work rejected at least `min` times and not landed since, with a rejection within `windowMs`. */
export function repeatedRejections(p: Pick<Pipeline, 'items' | 'recent'>, now: Ts, windowMs: number, min = 2): Chain[] {
  return pipelineChains(p).filter((c) => {
    if (c.rejected < min || c.outcome === 'landed' || c.outcome === 'cancelled') return false;
    const lastFail = Math.max(...c.attempts.filter((a) => a.state === 'failed').map((a) => a.endedAt ?? atOf(a)));
    return lastFail >= now - windowMs;
  });
}

/** "once", "twice", "3 times". */
export const times = (n: number): string => (n === 1 ? 'once' : n === 2 ? 'twice' : `${n} times`);

/** "Rejected twice, attempt 3 is verifying", "Landed on attempt 3 after 2 rejections". */
export function chainSummary(c: Chain, p: Pick<Pipeline, 'stages'>): string {
  const n = c.attempts.length;
  const stage = (p.stages.find((s) => s.id === c.latest.stage)?.title ?? c.latest.stage).toLowerCase();
  switch (c.outcome) {
    case 'landed':
      return c.rejected ? `Landed on attempt ${n} after ${plural(c.rejected, 'rejection')}` : `Landed on attempt ${n}`;
    case 'in-flight':
      return `${c.rejected ? `Rejected ${times(c.rejected)}, ` : ''}attempt ${n} is ${c.latest.state === 'queued' ? 'queued' : stage}`;
    case 'failed':
      return `Rejected ${times(c.rejected)}, not submitted again yet`;
    default:
      return `Withdrawn on attempt ${n}`;
  }
}
